import React, { useEffect } from "react";
import styled from "styled-components";
import { animateScroll as scroll } from "react-scroll";
import Testimonial from "../components/Sections/Testimonial";
import DemoCredentialButton from "../components/Elements/DemoCredentialButton";
import { APP_CREDENTIALS } from "../config/demoCredentials";
import {
  MdOutlinePersonSearch,
  MdOutlineQuestionAnswer,
  MdOutlineScore,
  MdOutlineAutoGraph,
} from "react-icons/md";

const AISalesTrainingPage = () => {
  useEffect(() => {
    scroll.scrollToTop();
  }, []);

  const handleTryDemo = () => {
    window.open(
      "https://demo.conversantech.com/ai-training-center/c-class",
      "_blank"
    );
  };

  const features = [
    {
      icon: <MdOutlinePersonSearch fontSize={26} />,
      title: "Dynamic Customer Personas",
      description:
        "Select from various AI-driven customer personas that mimic real-life showroom interactions.",
    },
    {
      icon: <MdOutlineQuestionAnswer fontSize={26} />,
      title: "Interactive Q&A Simulator",
      description:
        "Engage in back-and-forth conversations as the AI customer poses scenarios and questions.",
    },
    {
      icon: <MdOutlineScore fontSize={26} />,
      title: "Performance Scoring",
      description:
        "Receive an objective score reflecting your sales performance and handling of objections.",
    },
    {
      icon: <MdOutlineAutoGraph fontSize={26} />,
      title: "Constructive Feedback",
      description:
        "Get detailed, actionable feedback to improve pitch delivery and objection handling.",
    }
  ];

  return (
    <>
      <Wrapper className="flexNullCenter flexColumn">
        <HeroContainer>
          <ContentWrapper>
            <TextContent>
              <Title>AI Sales Training</Title>
              <Description>
                Revolutionize showroom sales training with our AI Sales Training application. Designed to hone the skills of sales representatives, the app allows users to choose different AI customer personas that realistically simulate real-world buyers. As the salesperson interacts with the AI through an immersive question-and-answer session, the virtual customer responds dynamically. At the conclusion of the session, the app provides a comprehensive score along with targeted feedback to help close gaps and perfect sales techniques.
              </Description>
              <ButtonWrapper>
                <DemoCredentialButton credentials={APP_CREDENTIALS.aiSalesTraining} />
                <TryButton onClick={handleTryDemo}>Try it</TryButton>
              </ButtonWrapper>
            </TextContent>
          </ContentWrapper>
        </HeroContainer>
      </Wrapper>

      <DemoSampleWrapper className="lightBg">
        <div className="container">
          <HeaderInfo>
            <h1 className="font40 extraBold">Features</h1>
            <p className="font13">
              Discover the powerful features designed to simulate real-world sales and effectively train your showroom team.
            </p>
          </HeaderInfo>
          <DemoSampleWrapperDiv>
            {features.map((feature, index) => (
              <FeatureWrapper
                key={index}
                className="whiteBg radius8 shadow"
              >
                <DemoFeatureDiv>
                  <DemoFeatureIcon>{feature.icon}</DemoFeatureIcon>
                  <DemoFeatureTitle className="font20 extraBold">
                    {feature.title}
                  </DemoFeatureTitle>
                </DemoFeatureDiv>
                <DemoFeatureDesc className="font13">
                  {feature.description}
                </DemoFeatureDesc>
              </FeatureWrapper>
            ))}
          </DemoSampleWrapperDiv>
        </div>
      </DemoSampleWrapper>

      {/* Testimonial */}
      <Testimonial />
    </>
  );
};

const Wrapper = styled.section`
  width: 100%;
  max-width: 1220px;
  margin: 0 auto;
  padding: 110px 30px 0px 30px;
  @media (max-width: 960px) {
    flex-direction: column;
  }
`;

const HeroContainer = styled.section`
  background-color: #f9fafb;
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 32px;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 32px;

  @media (max-width: 1024px) {
    flex-direction: column;
    text-align: center;
  }
`;

const TextContent = styled.div`
  flex: 1;
  min-width: 300px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 12px;
`;

const Description = styled.p`
  font-size: 18px;
  color: #6b7280;
  margin-bottom: 32px;
  line-height: 2;
`;

const DemoSampleWrapper = styled.div`
  margin-top: 50px;
  padding: 50px 0px;
`;

const HeaderInfo = styled.div`
  margin-bottom: 30px;
  @media (max-width: 860px) {
    text-align: center;
  }
`;

const DemoSampleWrapperDiv = styled.div`
  width: 100%;
  max-width: 1220px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const FeatureWrapper = styled.div`
  width: 350px;
  height: 220px;
  text-align: left;
  padding: 20px 30px;
  margin: 30px 0px;
  transition: transform 0.3s, box-shadow 0.3s;

  &:hover {
    transform: scale(1.05) translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  }
`;

const DemoFeatureDiv = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const DemoFeatureIcon = styled.div`
  width: 30%;
  width: 60px;
  height: 60px;
  background-color: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
`;

const DemoFeatureTitle = styled.h3`
  width: 70%;
  padding: 10px 15px;
`;

const DemoFeatureDesc = styled.p`
  padding: 10px 0px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
  gap: 16px;
  flex-wrap: wrap;
`;

const TryButton = styled.button`
  background-color: #7620ff;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.2s;
  box-shadow: 0 4px 6px rgba(118, 32, 255, 0.2);

  &:hover {
    background-color: #6010e2;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export default AISalesTrainingPage;
